import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const cardVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
      when: "beforeChildren"
    }
  },
  exit: { 
    opacity: 0, 
    y: 10,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 35,
      when: "afterChildren"
    }
  }
};

const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.025,
      delayChildren: 0.02,
      type: "spring",
      stiffness: 200,
      damping: 20
    }
  }
};

const elementVariants = {
  hidden: { opacity: 0, y: 5 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
      mass: 0.4
    }
  }
};

const BlogCardSkeleton = () => {
  return (
    <motion.div
      variants={cardVariants}
      className="bg-white rounded-lg shadow-lg overflow-hidden pointer-events-none"
    >
      <div className="animate-pulse">
        {/* Image skeleton */}
        <div className="w-full h-48 bg-gray-200" />
        
        {/* Content skeleton */}
        <div className="p-6">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const BlogCard = ({ post, type = 'blog' }) => {
  if (!post) return <BlogCardSkeleton />;

  const {
    title,
    date,
    source,
    excerpt,
    image,
    imageCaption,
    link
  } = post;

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
    >
      <Link to={link} className="block">
        <motion.div variants={contentVariants}>
          {/* Image */}
          <motion.div variants={elementVariants} className="relative aspect-square overflow-hidden">
            <img
              src={image?.fields?.file?.url ? `https:${image.fields.file.url}` : image}
              alt={imageCaption || title}
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
            />
          </motion.div>

          {/* Content */}
          <motion.div variants={elementVariants} className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{title}</h3>
            <div className="flex items-center text-sm text-gray-600 mb-3">
              <span className="mr-4">{source}</span>
              <span>{new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
            <p className="text-gray-600 mb-4 line-clamp-3">{excerpt}</p>
            <div className="flex items-center text-[#D3242A] font-medium">
              Read Article
              <svg 
                className="w-4 h-4 ml-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </div>
          </motion.div>
        </motion.div>
      </Link>
    </motion.div>
  );
};

export default BlogCard;
